import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SALES_URL = `${API_URL}/sales-orders`;
const LOV_URL = `${API_URL}/commonLOVs`;
const SALES_ORDER_LINE_URL = `${API_URL}/sales-order-lines`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const salesOrderService = {
  // Get all sales orders with filters
  getAllOrders: async (filters = {}) => {
    try {
      const response = await axios.get(SALES_URL, {
        headers: getAuthHeader(),
        params: filters
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch sales orders' };
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      const response = await axios.get(`${SALES_URL}/byId/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order' };
    }
  },

  // Create new order with lines
  createOrder: async (orderData) => {
    try {
      const response = await axios.post(SALES_URL, orderData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create order' };
    }
  },

  // Update order
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await axios.put(`${SALES_URL}/${orderId}`, orderData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update order' };
    }
  },

  // Hard-delete a channel-3 store partner order and all related data (SADM only)
  deleteStoreOrder: async (orderId) => {
    try {
      const response = await axios.delete(`${SALES_URL}/store-order/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete store partner order' };
    }
  },

  // Delete order
  deleteOrder: async (orderId) => {
    try {
      const response = await axios.delete(`${SALES_URL}/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete order' };
    }
  },

  // Get all sales channels
  getSalesChannels: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/saleschannels`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch sales channels' };
    }
  },

  // Get all customers
  getCustomers: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/lhcustomers`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch customers' };
    }
  },

  // Get products by subsidiary
  getProducts: async (subsidiary) => {
    try {
      const response = await axios.get(`${LOV_URL}/lhproducts/${subsidiary}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch products' };
    }
  },

  // Get order lines by order ID
  getOrderLines: async (orderId) => {
    try {
      const response = await axios.get(`${SALES_ORDER_LINE_URL}/byOrderId/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order lines' };
    }
  },

  // Create order line
  createOrderLine: async (lineData) => {
    try {
      const response = await axios.post(SALES_ORDER_LINE_URL, lineData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create order line' };
    }
  },

  // Update order line
  updateOrderLine: async (lineId, lineData) => {
    try {
      const response = await axios.put(`${SALES_ORDER_LINE_URL}/${lineId}`, lineData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update order line' };
    }
  },

  // Delete order line
  deleteOrderLine: async (lineId, orderId) => {
    try {
      const response = await axios.delete(`${SALES_ORDER_LINE_URL}/${lineId}/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete order line' };
    }
  },

  // Update order_lc_state only
  updateLcState: async (orderId, order_lc_state) => {
    try {
      const response = await axios.patch(`${SALES_URL}/${orderId}/lc-state`, { order_lc_state }, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update order state' };
    }
  },

  // Check customer LOV flag for sales channel
  checkCustomerLovFlag: async (salesChannelId) => {
    try {
      const response = await axios.get(`${SALES_URL}/customerlovyn/${salesChannelId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to check customer LOV flag' };
    }
  },

  // Get assignments by order ID
  getAssignmentsByOrderId: async (orderId) => {
    try {
      const response = await axios.get(`${SALES_URL}/salesOrderAssignments/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch assignments' };
    }
  },

  // Create assignment
  createAssignment: async (assignmentData) => {
    try {
      const response = await axios.post(`${SALES_URL}/salesOrderAssignments`, assignmentData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create assignment' };
    }
  },

  // Delete assignment
  deleteAssignment: async (assignmentId) => {
    try {
      const response = await axios.delete(`${SALES_URL}/salesOrderAssignments/${assignmentId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete assignment' };
    }
  },

  // Get aggregated order lines across multiple orders
  getMultipleOrderLines: async (orderIds) => {
    try {
      const response = await axios.get(`${SALES_URL}/multipleOrderLines`, {
        headers: getAuthHeader(),
        params: { order_ids: orderIds.join(',') }
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order lines' };
    }
  },

  // Get users list
  getUsers: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/lhusers`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users' };
    }
  }
};

export default salesOrderService;
